import React, {
  createContext,
  useContext,
  useMemo,
  type ReactNode,
} from "react";
import {
  useBoardViewPreferencesViewModel,
  type BoardPreferencesStorage,
  type BoardViewPreferencesViewModel,
} from "@onetasklist/shared";
import { useBoardId } from "./BoardContext";

const BoardFiltersContext = createContext<BoardViewPreferencesViewModel | null>(
  null,
);

/**
 * Provides a single shared filter state (useBoardViewPreferencesViewModel) to
 * the entire board subtree — covering both the sidebar and the board content.
 * Mount this inside BoardProvider so that both AppSidebar and Outlet (BoardView)
 * share one filter state instead of maintaining separate, out-of-sync instances.
 */
export const BoardFiltersProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const boardId = useBoardId();

  // Preferences are stored per board: collapsed columns are meaningless on
  // another board, and a density or sort that suits a 10-column board rarely
  // suits a 3-column one. Reads fall back to the old unscoped key so settings
  // saved before this change survive as the starting point for every board;
  // writes only ever go to the scoped key.
  const storage = useMemo<BoardPreferencesStorage | undefined>(() => {
    if (typeof window === "undefined") return undefined;
    const prefix = boardId ? `board.${boardId}.` : "";
    return {
      getItem: (key: string) =>
        window.localStorage.getItem(`${prefix}${key}`) ??
        window.localStorage.getItem(key),
      setItem: (key: string, value: string) => {
        window.localStorage.setItem(`${prefix}${key}`, value);
      },
    };
  }, [boardId]);

  const filterState = useBoardViewPreferencesViewModel({ storage });

  return (
    <BoardFiltersContext.Provider value={filterState}>
      {children}
    </BoardFiltersContext.Provider>
  );
};

/**
 * Returns the shared board filter state.
 * Must be called within a BoardFiltersProvider.
 */
export const useBoardFilters = (): BoardViewPreferencesViewModel => {
  const ctx = useContext(BoardFiltersContext);
  if (ctx === null) {
    throw new Error(
      "useBoardFilters must be used within a BoardFiltersProvider",
    );
  }
  return ctx;
};
